import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * CircularProgress - Circular progress indicator (ring)
 *
 * An SVG-based circular progress indicator with optional percentage display.
 * Perfect for readiness scores, completion percentages, and gauge visualizations.
 *
 * @example
 * ```tsx
 * <CircularProgress
 *   value={92}
 *   size="md"
 *   color="emerald"
 *   showValue
 * />
 * ```
 */

export interface CircularProgressProps {
  /** Progress value (0-100) */
  value: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Color variant */
  color?: 'blue' | 'violet' | 'emerald' | 'amber' | 'cyan' | 'rose' | 'gray';
  /** Show percentage value in center */
  showValue?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeMap = {
  sm: { dimension: 36, radius: 15.9155, center: 18 },
  md: { dimension: 48, radius: 21.221, center: 24 },
  lg: { dimension: 64, radius: 28.2945, center: 32 },
};

const colorMap = {
  blue: '#3b82f6',
  violet: '#8b5cf6',
  emerald: '#10b981',
  amber: '#f59e0b',
  cyan: '#06b6d4',
  rose: '#f43f5e',
  gray: '#9ca3af',
};

const textColorMap = {
  blue: 'text-blue-600',
  violet: 'text-violet-600',
  emerald: 'text-emerald-600',
  amber: 'text-amber-600',
  cyan: 'text-cyan-600',
  rose: 'text-rose-600',
  gray: 'text-gray-500',
};

/**
 * CircularProgress Component
 *
 * Features:
 * - SVG-based circular progress ring
 * - Uses stroke-dasharray for percentage rendering
 * - Optional centered percentage value
 * - Multiple size variants
 * - Color-coded by status/category
 * - Smooth animations via CSS transitions
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 'md',
  strokeWidth = 3,
  color = 'blue',
  showValue = false,
  className,
}) => {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(Math.max(value, 0), 100);

  const { dimension, radius, center } = sizeMap[size];
  const strokeDasharray = `${clampedValue}, 100`;

  return (
    <div className={cn('readiness-ring relative inline-flex', className)}>
      <svg
        width={dimension}
        height={dimension}
        viewBox={`0 0 ${dimension} ${dimension}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <path
          d={`M ${center} ${center - radius} a ${radius} ${radius} 0 0 1 0 ${
            radius * 2
          } a ${radius} ${radius} 0 0 1 0 -${radius * 2}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <path
          d={`M ${center} ${center - radius} a ${radius} ${radius} 0 0 1 0 ${
            radius * 2
          } a ${radius} ${radius} 0 0 1 0 -${radius * 2}`}
          fill="none"
          stroke={colorMap[color]}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dasharray 0.3s ease-in-out',
          }}
        />
      </svg>
      {/* Centered value */}
      {showValue && (
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center text-xs font-bold',
            textColorMap[color]
          )}
        >
          {Math.round(clampedValue)}
        </span>
      )}
    </div>
  );
};

CircularProgress.displayName = 'CircularProgress';
